import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ShieldAlert, CheckCircle, XCircle, List } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../utils/api';
import StatCard from '../../components/ui/StatCard';
import StatusChip from '../../components/ui/StatusChip';
import InspectModal from './InspectModal';

const tabs = ['all', 'quarantined', 'cleared', 'rejected'];

const QuarantineList = () => {
  const [items, setItems]     = useState([]);
  const [stats, setStats]     = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('all');
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab !== 'all' ? { status: tab } : {};
      const [iRes, sRes] = await Promise.all([
        api.get('/quarantine', { params }),
        api.get('/quarantine/stats'),
      ]);
      setItems(iRes.data.data);
      setStats(sRes.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    {
      label: 'Item Name', key: 'itemName',
      render: (r) => <span className="font-medium text-zip-text">{r.itemName}</span>,
    },
    {
      label: 'Qty', key: 'qty',
      render: (r) => <span className="font-mono text-zip-muted text-xs">{r.quantity} {r.unit}</span>,
    },
    {
      label: 'Invoice', key: 'invoice',
      render: (r) => (
        <span className="font-mono text-zip-red text-xs">{r.inwardReceipt?.invoiceNumber ?? '—'}</span>
      ),
    },
    {
      label: 'Reason', key: 'reason',
      render: (r) => (
        <span className="text-zip-muted text-xs truncate max-w-[120px] block">{r.reason ?? '—'}</span>
      ),
    },
    { label: 'Status', key: 'status', render: (r) => <StatusChip status={r.status} /> },
    {
      label: 'Date', key: 'createdAt',
      render: (r) => (
        <span className="font-mono text-zip-muted text-xs">
          {format(new Date(r.createdAt), 'dd MMM')}
        </span>
      ),
    },
    {
      label: 'Inspected By', key: 'inspectedBy',
      render: (r) => r.inspectedBy?.name ?? <span className="text-zip-muted">—</span>,
    },
    {
      label: '', key: 'actions',
      render: (r) =>
        r.status === 'quarantined' ? (
          <button
            onClick={() => setSelected(r)}
            className="text-xs btn-primary py-1 px-3"
          >
            Inspect
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page ambient glow */}
      <div
        className="relative -mx-5 lg:-mx-7 -mt-5 lg:-mt-7 px-5 lg:px-7 pt-6 pb-5 mb-0"
        style={{
          background: 'radial-gradient(ellipse 60% 80px at 30% 0%, rgba(232,25,44,0.04) 0%, transparent 100%)',
        }}
      >
        <h2 className="text-zip-muted text-xs font-mono uppercase tracking-widest mb-4">
          Quarantine Control
        </h2>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label="Total"       value={stats.total ?? 0}       icon={List} />
          <StatCard label="Quarantined" value={stats.quarantined ?? 0} icon={ShieldAlert} />
          <StatCard label="Cleared"     value={stats.cleared ?? 0}     icon={CheckCircle} />
          <StatCard label="Rejected"    value={stats.rejected ?? 0}    icon={XCircle} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 capitalize
              ${tab === t
                ? 'bg-zip-red text-white'
                : 'border border-zip-border text-zip-muted hover:text-zip-text hover:bg-zip-hover'
              }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Table with per-row left border on quarantined */}
      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 skeleton rounded" />
          ))}
        </div>
      ) : (
        <div className="zip-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zip-surface border-b border-zip-border">
                  {columns.map((col) => (
                    <th key={col.key ?? col.label} className="zip-th">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="zip-td text-center text-zip-muted py-12">
                      No quarantine items found
                    </td>
                  </tr>
                ) : (
                  items.map((row, i) => (
                    <motion.tr
                      key={row._id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04, duration: 0.18 }}
                      className={`border-b border-zip-border last:border-0 hover:bg-zip-hover transition-colors duration-[120ms]
                        ${row.status === 'quarantined' ? 'border-l-[3px] border-l-zip-red' : ''}`}
                    >
                      {columns.map((col) => (
                        <td key={col.key ?? col.label} className="zip-td">
                          {col.render ? col.render(row) : row[col.key]}
                        </td>
                      ))}
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <InspectModal
          item={selected}
          onClose={() => setSelected(null)}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
};

export default QuarantineList;
